import classNames from 'classnames';
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import IconArrowHead from '../IconArrowHead/IconArrowHead';
import IconCheckmark from '../IconCheckmark/IconCheckmark';
import { FormattedMessage } from 'react-intl';

import css from './Dropdown.module.css';

const escapeRegExp = string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const makeSubstringBold = (text, shouldBeBold) => {
  if (!shouldBeBold) return text;

  const escaped = escapeRegExp(shouldBeBold);
  const regex = new RegExp(escaped, 'ig');
  const textArray = text.split(regex);
  const match = text.match(regex);

  return (
    <span>
      {textArray.map((item, index) => (
        <React.Fragment key={index}>
          {item}
          {index !== textArray.length - 1 && match && <b>{match[index]}</b>}
        </React.Fragment>
      ))}
    </span>
  );
};

const Dropdown = props => {
  const {
    search,
    onSearch,
    className,
    dropdownInputClassName,
    inputIcon,
    iconClassName,
    placeholder,
    onChange,
    onFocus,
    onBlur,
    name,
    id,
    disableRest,
    disabled,
    currentOptionMessageId,
    filterType = 'startsWith',
    enableCurrentOptionSelect = false,
    value = null,
    options = [],
    customIcon = null,
    searchable = true,
    disableFiltering = false,
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchString, setSearchString] = useState(search ?? getOptionByValue(value)?.label ?? '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasTyped, setHasTyped] = useState(false);

  const shouldForceFilter = options.length > 20;

  const dropdownRef = useRef(null);
  const optionsRefs = useRef([]);
  const listRef = useRef(null);

  const dropdownClasses = classNames(className, css.dropdown, {
    [css.dropdownOpen]: dropdownOpen,
    [css.forcedFilter]: shouldForceFilter,
  });

  const chooseOption = useCallback(
    newValue => {
      onChange(newValue);
      setSearchString(newValue);
      setDropdownOpen(false);
    },
    [onChange, value]
  );

  const removeOption = useCallback(() => {
    onChange(undefined);
    setDropdownOpen(false);
  }, [onChange]);

  const optionChecked = useCallback(option => value === option.value, [value]);

  const optionDisabled = useCallback(option => disableRest && !optionChecked(option), [
    disableRest,
    optionChecked,
  ]);

  const handleOptionClicked = useCallback(
    option => {
      if (optionDisabled(option)) return;
      !optionChecked(option) ? chooseOption(option.value) : removeOption();
    },
    [chooseOption, optionChecked, optionDisabled, removeOption]
  );

  const handleSearch = searchValue => {
    setSearchString(searchValue);
    if (shouldForceFilter) {
      setHasTyped(searchValue.trim().length > 0);
    }
  };

  function getOptionByValue(value) {
    return options.find(option => option.value === value);
  }

  const scrollIntoViewIfNeeded = target => {
    if (!listRef.current || !target) return;
    const listBounds = listRef.current.getBoundingClientRect();
    const targetBounds = target.getBoundingClientRect();

    if (targetBounds.bottom > listBounds.bottom) {
      target.scrollIntoView(false);
    } else if (targetBounds.top < listBounds.top) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  };

  const filterOptionsBasedOnSearchString = useCallback(
    searchString => option => {
      if (!searchString || !searchable || disableFiltering) return true;
      return option.label.toLowerCase()[filterType](searchString.toLowerCase());
    },
    [searchable, disableFiltering]
  );

  const filteredOptions = useMemo(() => {
    const filtered = options.filter(filterOptionsBasedOnSearchString(searchString));
    return [...filtered].sort((a, b) => {
      if (a.value === 'other') return 1;
      if (b.value === 'other') return -1;
      return a.label?.localeCompare(b.label ?? '') ?? 0;
    });
  }, [searchString, options, filterOptionsBasedOnSearchString]);

  useEffect(() => {
    const closeDropdownOnClickOutside = e => {
      if (dropdownOpen && !dropdownRef.current?.contains(e.target)) {
        setDropdownOpen(false);
        onBlur?.();
      }
    };

    const keyDownCallback = e => {
      if (!dropdownOpen) return;

      let newIndex;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newIndex = activeIndex > 0 ? activeIndex - 1 : activeIndex;
          scrollIntoViewIfNeeded(optionsRefs.current[newIndex]);
          setActiveIndex(newIndex);
          return;
        case 'ArrowDown':
          e.preventDefault();
          newIndex = activeIndex < options.length - 1 ? activeIndex + 1 : activeIndex;
          scrollIntoViewIfNeeded(optionsRefs.current[newIndex]);
          setActiveIndex(newIndex);
          return;
        case 'Enter':
          e.preventDefault();
          if (activeIndex < 0) return;
          handleOptionClicked(options.find((option, index) => index === activeIndex));
          return;
        default:
          return;
      }
    };

    document.addEventListener('mousedown', closeDropdownOnClickOutside);
    document.addEventListener('keydown', keyDownCallback);
    return () => {
      document.removeEventListener('mousedown', closeDropdownOnClickOutside);
      document.removeEventListener('keydown', keyDownCallback);
    };
  }, [activeIndex, dropdownOpen, handleOptionClicked, onBlur, options]);

  useEffect(() => {
    const option = getOptionByValue(value);
    setSearchString(option?.label ?? option?.value ?? '');
  }, [value]);

  useEffect(() => {
    setSearchString(search);
  }, [search]);

  useEffect(() => {
    if (!dropdownOpen) {
      setHasTyped(false);
    }
  }, [dropdownOpen]);

  return (
    <div className={dropdownClasses} ref={dropdownRef}>
      <div className={css.dropdownTrigger}>
        {inputIcon && <span className={iconClassName}>{inputIcon}</span>}

        {searchable ? (
          <input
            id={id}
            name={name}
            className={classNames(dropdownInputClassName, css.input)}
            value={searchString}
            onChange={e => {
              if (searchable) {
                handleSearch(e.target.value);
                onSearch?.(e.target.value);
              }
            }}
            onFocus={e => {
              setDropdownOpen(true);
              onFocus?.(e);
            }}
            placeholder={placeholder}
            readOnly={!searchable}
            disabled={disabled}
            autoComplete="off"
          />
        ) : (
          <div
            className={classNames(
              css.dropdownLabel,
              {
                [css.dropdownLabelDisabled]: disabled,
                [css.dropdownLabelPlaceholderStyle]: !value,
              },
              dropdownInputClassName
            )}
            onClick={e => {
              if (disabled) return;
              if (!dropdownOpen) {
                setDropdownOpen(true);
                onFocus?.(e);
              } else {
                setDropdownOpen(false);
                onBlur?.(e);
              }
            }}
          >
            {getOptionByValue(value)?.label || placeholder}
          </div>
        )}

        <button
          type="button"
          className={css.openBtn}
          onClick={() => {
            if (dropdownOpen) onBlur?.();
            setDropdownOpen(prev => !prev);
          }}
        >
          {customIcon ??
            (!shouldForceFilter && (
              <IconArrowHead direction={!dropdownOpen ? 'down' : 'up'} className={css.arrowIcon} />
            ))}
        </button>
      </div>
      {dropdownOpen && (
        <ul className={css.optionsList} ref={listRef}>
          {enableCurrentOptionSelect && searchString && (
            <li
              ref={element => (optionsRefs.current[0] = element)}
              onClick={() => {
                handleOptionClicked({ label: searchString, value: searchString });
                onBlur?.();
              }}
              className={css.option}
              role="option"
              aria-selected={0 === activeIndex}
              aria-disabled={optionDisabled({ label: searchString, value: searchString })}
              onMouseMove={() => {
                setActiveIndex(0);
              }}
            >
              {currentOptionMessageId ? (
                <FormattedMessage
                  id={currentOptionMessageId}
                  values={{ value: makeSubstringBold(searchString, searchString) }}
                />
              ) : (
                makeSubstringBold(searchString, searchString)
              )}
            </li>
          )}

          {(!shouldForceFilter || hasTyped) &&
            filteredOptions.map((option, index) => {
              const indexFinal = enableCurrentOptionSelect && searchString ? index + 1 : index;
              return (
                <li
                  key={option.value}
                  ref={element => (optionsRefs.current[indexFinal] = element)}
                  onClick={() => {
                    handleOptionClicked(option);
                    onBlur?.();
                  }}
                  className={css.option}
                  role="option"
                  aria-selected={indexFinal === activeIndex}
                  aria-disabled={optionDisabled(option)}
                  onMouseMove={() => {
                    setActiveIndex(indexFinal);
                  }}
                >
                  {option.icon && <span className={css.optionIcon}>{option.icon}</span>}
                  {makeSubstringBold(option.label, searchString)}

                  {optionChecked(option) && (
                    <IconCheckmark size="small" className={css.checkIcon} />
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  id: string,
  className: string,
  dropdownInputClassName: string,
  inputIcon: node,
  placeholder: string,
  onChange: func,
  onBlur: func,
  onFocus: func,
  disableRest: bool,
  customIcon: node,
  options: arrayOf(
    shape({
      label: oneOfType([string, node]).isRequired,
      searchString: string,
      value: oneOfType([string, arrayOf(string)]).isRequired,
      icon: node,
      group: string,
    })
  ),
  value: string,
};

export default Dropdown;
